// [2026-09-03] ADDED — the page an invitation link opens.
//
// WHY THE TOKEN IS IN THE URL FRAGMENT (#t=…) AND NOT A QUERY STRING.
// A fragment is never transmitted to any server. It is absent from this service's access logs, from
// every proxy and CDN in front of it, and from the `Referer` header of anything this page
// subsequently loads — an analytics script, a font, an image. A query string is present in all of
// them. The token is single-use and expires in seven days regardless; this costs nothing and closes
// the most likely way one would leak, which is not an attack but a log file somebody grepped.
//
// AND WHY IT IS STRIPPED IMMEDIATELY. Read once, then removed from the address bar with
// history.replaceState before anything else runs. So it is not in the browser history, not in a
// screenshot of the address bar, and not in whatever the user pastes when they ask for help.
//
// WHAT THIS PAGE DOES NOT DO: decide anything. It presents a token; the server checks it against
// the signed-in person's verified email, its expiry and its single-use state. A forwarded
// invitation opened by the wrong person fails here no matter what this file does.

import { useEffect, useRef, useState } from 'react'
import { platform } from '../api.js'

/** Pull the token out of the fragment and clear it from the address bar in the same breath. */
function takeToken() {
  const raw = window.location.hash || ''
  if (!raw.startsWith('#')) return ''
  const token = new URLSearchParams(raw.slice(1)).get('t') || ''
  if (token) {
    // replaceState, not pushState: this must not leave a history entry the Back button can return
    // to with the token restored.
    window.history.replaceState(null, '', window.location.pathname)
  }
  return token
}

export default function AcceptInvite({ onDone }) {
  const [state, setState] = useState({ status: 'working', message: '' })
  // StrictMode double-invokes effects in development, and this effect SPENDS a single-use token.
  // Without this guard the second run redeems an already-used token and shows a failure for an
  // invitation that actually worked.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const token = takeToken()
    if (!token) {
      setState({
        status: 'error',
        message: 'This link is incomplete. Open the invitation from your email again, or ask for a new one.',
      })
      return
    }

    platform.acceptInvite(token)
      .then((data) => {
        setState({
          status: 'ok',
          message: `You now have access as ${data.role || 'a member'}.`,
        })
      })
      .catch((err) => {
        // 401 is a DIFFERENT problem from a bad token and needs a different sentence: the person is
        // simply not signed in yet. Telling them their invitation is invalid would send them back to
        // whoever invited them for a replacement that would fail the same way.
        const notSignedIn = err?.code === 'NOT_AUTHENTICATED' || err?.status === 401
        setState({
          status: notSignedIn ? 'signin' : 'error',
          message: notSignedIn
            ? 'Sign in to your InCruiter account first, then open the invitation link again.'
            : (err?.message || 'This invitation could not be used.'),
        })
      })
  }, [])

  const heading = {
    working: 'Checking your invitation…',
    ok: 'You are in',
    signin: 'Sign in to continue',
    error: 'This invitation did not work',
  }[state.status]

  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24,
      background: 'var(--page, #f4f5f7)',
      fontFamily: '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif',
    }}>
      <div style={{
        width: '100%', maxWidth: 460, background: 'var(--card, #fff)', borderRadius: 12,
        padding: '32px 28px', boxShadow: '0 1px 3px rgba(16,24,40,.1)', textAlign: 'center',
      }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--accent, #1e2a5a)',
                      marginBottom: 18 }}>
          InCruiter Proctoring
        </div>
        <h1 style={{ margin: '0 0 10px', fontSize: 20, color: 'var(--ink, #111827)' }}>
          {heading}
        </h1>
        <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.6,
                    color: 'var(--muted-fg, #6b7280)' }}>
          {state.message || 'One moment.'}
        </p>

        {state.status === 'ok' && (
          <button onClick={onDone}
                  style={{ padding: '11px 22px', borderRadius: 8, border: 0, fontSize: 15,
                           fontWeight: 600, color: '#fff', background: 'var(--accent, #1e2a5a)',
                           cursor: 'pointer' }}>
            Open the dashboard
          </button>
        )}

        {/* Every terminal state gets an action. A page that can be reached and then not moved on
            from is a dead end, and this one is reached from an email — the person has no tab to go
            back to. */}
        {(state.status === 'error' || state.status === 'signin') && (
          <button onClick={onDone}
                  style={{ padding: '11px 22px', borderRadius: 8, fontSize: 15, fontWeight: 600,
                           cursor: 'pointer', background: 'none',
                           border: '1px solid var(--border, #d5d8dd)',
                           color: 'var(--ink, #111827)' }}>
            Go to the dashboard
          </button>
        )}
      </div>
    </div>
  )
}
